import { useCallback, useEffect } from 'react';
import type { Session } from '@fanduel/cpe-um-bundle/account/contract';
import { Account } from '@fanduel/cpe-um-bundle/account/library';
import { jwtDecode } from 'jwt-decode';

import { updateAuthenticationToken } from '@/api/authentication/update-authentication-token';
import { isEmbeddedInLobby } from '@/logic/embedded-mode';
import {
  hydrateEmbeddedSession,
  requestEmbeddedSessionKeepAlive,
} from '@/logic/web-wrapper/embedded-session';
import { resolveWebWrapper } from '@/logic/web-wrapper/web-wrapper-bridge';

import {
  initializeAccountModule,
  initializeUnifiedModules,
} from '../unified-modules-core';
import { applyUmSessionToStore } from './annexes/session-manager';
import { clearSessionAndRedirectToLogin } from '@/utilities/helpers/clearSession';
import {
  bootstrapMockHarnessSession,
  CLEAR_SESSION_REASON,
  getLoginToken,
} from '@/unified-modules/account/account-library';
import { shouldEnforceFabricAuthCookies } from '@/unified-modules/account/annexes/fabric-auth-cookies';
import {
  resetGameStore,
  resetGeolocationStore,
  resetUserSessionStore,
  setGameWindowContext,
  setUserSessionContext,
} from '@/store/actions';
import { useStore } from '@/store/useStore';
import { GameStoreParts } from '@/store/slices/game/types';
import {
  getAWAuthTokens,
  getLocalAccountSession,
} from '@/utilities/getters/sessionContextGetters';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';
import {
  removeCookieSessionContext,
  setCookieSessionContext,
} from '@/utilities/setters/cookieSetters';
import reloadIframe from '@/lostHooks/softReload/reloadIframe';
import { invalidateGlsLaunch } from '@/game-client/gls-launch-guard';
import { clearVitalsStorage } from '@/utilities/logger/datadog/vitals';
import { usePopup } from '@/features/Popup/usePopup';
import { hasLobbySessionCookies } from '@/unified-modules/account/annexes/cookie-session';
import { clearStaleSession } from '@/utilities/helpers/clearStaleSession';
import { ERROR_TYPE } from '@/features/Popup/constants';
import { promptAccountLogin } from '@/unified-modules/account/prompt-account-login';

type UseAccountUmOptions = {
  /**
   * Run Account UM init + session subscription once (HomeRouteEffects only).
   * Other call sites should use the returned methods without bootstrapping.
   */
  bootstrap?: boolean;
};

export const SESSION_PHASE = {
  INIT: 'init',
  REFRESH: 'refresh',
} as const;

export type SessionPhase = (typeof SESSION_PHASE)[keyof typeof SESSION_PHASE];

let initialized = false;
let initializationPromise: Promise<void> | null = null;

export const __testResetAccountUm = () => {
  initialized = false;
  initializationPromise = null;
};

const getEmbeddedSession = async (): Promise<Session | undefined> => {
  const wrapper = resolveWebWrapper();
  const session = await wrapper?.getSession();
  if (!session?.jwt || !session.sessionId) return undefined;

  return {
    token: session.jwt,
    sessionId: session.sessionId,
    created: session.created,
  } as unknown as Session;
};

const getSessionTokenContext = (): {
  sessionTokenExpiry?: string;
  sessionTokenExpired?: boolean;
} => {
  try {
    const { authToken } = getAWAuthTokens();
    if (!authToken) return {};

    const { exp } = jwtDecode<{ exp?: number }>(authToken);
    if (!exp) return {};

    const expiresAtMs = exp * 1000;
    return {
      sessionTokenExpiry: new Date(expiresAtMs).toISOString(),
      sessionTokenExpired: expiresAtMs <= Date.now(),
    };
  } catch {
    return {};
  }
};

export const resetSessionConsumers = (shouldReloadIframe = true) => {
  clearVitalsStorage();
  setGameWindowContext({ gameWindowOpen: false });
  invalidateGlsLaunch();
  resetUserSessionStore();
  resetGeolocationStore();
  resetGameStore(GameStoreParts.clientStatus, { skipRevisionBump: true });
  resetGameStore(GameStoreParts.gameStatus, { skipRevisionBump: true });
  resetGameStore(GameStoreParts.gameError, { skipRevisionBump: true });
  resetGameStore(GameStoreParts.gameContext, { skipRevisionBump: false });

  if (shouldReloadIframe) {
    reloadIframe();
  }
};

const handleInvalidSession = async (
  phase: SessionPhase,
  hidePopup: () => void,
) => {
  Logger.logRumAction({
    name: '[GW][SESSION] Invalidated',
    context: { phase },
    useCase: DdEventCategory.Session,
    eventType: DdEventType.Info,
  });

  await clearSessionAndRedirectToLogin(
    CLEAR_SESSION_REASON.EXPIRY_TIMER,
    hidePopup,
  );
};

type EmbeddedSessionReconciliation = {
  session?: Session;
  valid: boolean;
};

const reconcileEmbeddedSession = async (
  hidePopup: () => void,
): Promise<EmbeddedSessionReconciliation> => {
  const previousSession = useStore.getState().userSessionContext;
  const hydrated = await hydrateEmbeddedSession();

  if (!previousSession.hasActiveSession) {
    return {
      session: hydrated ? await getEmbeddedSession() : undefined,
      valid: hydrated,
    };
  }

  if (!hydrated) {
    removeCookieSessionContext();
    await initializeUnifiedModules();
    await handleInvalidSession(SESSION_PHASE.REFRESH, hidePopup);
    return { valid: false };
  }

  const currentSession = useStore.getState().userSessionContext;
  if (
    previousSession.sessionId !== currentSession.sessionId ||
    previousSession.userId !== currentSession.userId
  ) {
    resetSessionConsumers(false);
    const rehydrated = await hydrateEmbeddedSession();
    if (!rehydrated) {
      removeCookieSessionContext();
      await initializeUnifiedModules();
      await handleInvalidSession(SESSION_PHASE.REFRESH, hidePopup);
      return { valid: false };
    }
    reloadIframe();
    return {
      session: await getEmbeddedSession(),
      valid: true,
    };
  }

  return {
    session: await getEmbeddedSession(),
    valid: true,
  };
};

const hasUmSessionIdentityChanged = (
  session: Session,
  previousSession: ReturnType<typeof useStore.getState>['userSessionContext'],
): boolean => {
  if (
    !previousSession.hasActiveSession ||
    !previousSession.sessionId ||
    !previousSession.userId
  ) {
    return false;
  }

  const nextUserId = session.userId ? String(session.userId) : null;

  return (
    session.sessionId !== previousSession.sessionId ||
    (nextUserId !== null && nextUserId !== previousSession.userId)
  );
};

const handleSession = async (
  session: Session | undefined,
  phase: SessionPhase,
  hidePopup: () => void,
) => {
  const hadActiveSession =
    useStore.getState().userSessionContext.hasActiveSession;

  if (session) {
    if (!session.token) {
      return;
    }

    const previousSession = useStore.getState().userSessionContext;
    const identityChanged = hasUmSessionIdentityChanged(
      session,
      previousSession,
    );

    if (identityChanged) {
      removeCookieSessionContext();
      resetSessionConsumers(false);
    }

    applyUmSessionToStore(session);

    const loginTokenJwt =
      (await getLoginToken()) ??
      useStore.getState().userSessionContext.loginToken?.jwt ??
      getLocalAccountSession()?.loginToken?.jwt;

    if (loginTokenJwt) {
      setUserSessionContext({ loginToken: { jwt: loginTokenJwt } });
    }

    if (
      shouldEnforceFabricAuthCookies() &&
      phase === SESSION_PHASE.REFRESH &&
      session.sessionId
    ) {
      setCookieSessionContext({
        authToken: { jwt: session.token },
        ...(loginTokenJwt ? { loginToken: { jwt: loginTokenJwt } } : {}),
        sessionId: session.sessionId,
      });
    }

    Logger.logRumAction({
      name: `[GW][SESSION] ${hadActiveSession ? 'Extended' : 'New'}`,
      description: hadActiveSession
        ? 'Session was extended successfully.'
        : 'Session was created successfully.',
      context: {
        phase,
        sessionId: session.sessionId,
        expires: session.expires,
      },
      useCase: DdEventCategory.Session,
      eventType: DdEventType.Info,
    });

    await updateAuthenticationToken(session.token);

    if (identityChanged) {
      reloadIframe();
    }

    return;
  }

  if (!hadActiveSession) {
    setUserSessionContext({ hasActiveSession: false });
    return;
  }

  await handleInvalidSession(phase, hidePopup);
};

const getSession = async (loginToken: string) => {
  let session: Session | undefined;
  const { isMobile } = useStore.getState().runtime.wrapperType;

  if (isMobile && loginToken) {
    session = await Account.getSessionAsync({ loginToken });
  } else if (!isMobile) {
    session = await Account.getSessionAsync();
  } else {
    session = await Account.getSessionAsync('Silent');
  }

  return session ?? (await Account.getSessionAsync({ loginToken }));
};

const initializeAccountUM = async (hidePopup: () => void) => {
  if (initialized) return;

  if (initializationPromise) {
    await initializationPromise;
    return;
  }

  initializationPromise = (async () => {
    if (isEmbeddedInLobby()) {
      const { session } = await reconcileEmbeddedSession(hidePopup);
      if (session) {
        await handleSession(session, SESSION_PHASE.INIT, hidePopup);
      }
      void initializeUnifiedModules()
        .then(() =>
          Account.subscribeSession((nextSession) =>
            handleSession(nextSession, SESSION_PHASE.REFRESH, hidePopup),
          ),
        )
        .then(() => Account.checkValidSession())
        .catch(() => undefined);

      // Align GW UM with lobby cookies after embedded bootstrap (standalone path
      // runs this after subscribe as well).

      initialized = true;
      return;
    }

    // A mock-harness build seeds its own session; nothing left to initialise.
    if (await bootstrapMockHarnessSession()) {
      initialized = true;
      return;
    }

    void initializeUnifiedModules().catch(() => undefined);
    await initializeAccountModule();

    const { authToken, loginToken } = getAWAuthTokens();

    const session = await getSession(loginToken);

    const hadOptimisticSession =
      useStore.getState().userSessionContext.hasActiveSession;

    await handleSession(session, SESSION_PHASE.INIT, hidePopup);

    await Account.subscribeSession((nextSession) =>
      handleSession(nextSession, SESSION_PHASE.REFRESH, hidePopup),
    );

    await Account.checkValidSession();

    initialized = true;

    if (!session && !hadOptimisticSession) {
      if (!authToken) {
        clearStaleSession();
      }

      const { isAndroid, isXsell } = useStore.getState().runtime.wrapperType;
      if (!isAndroid && !isXsell && !hasLobbySessionCookies()) {
        void promptAccountLogin({
          reason: 'no_session_at_init',
          phase: SESSION_PHASE.INIT,
          hadActiveSessionBeforeClear: hadOptimisticSession,
        });
      }
    }
  })();

  try {
    await initializationPromise;
  } finally {
    initializationPromise = null;
  }
};

const ensureInitialization = async (hidePopup: () => void) => {
  await initializeAccountUM(hidePopup);
};

export const reinitAccountUmSession = async (
  trigger: 'visibility' | 'interval',
  hidePopup: () => void,
) => {
  if (
    typeof document !== 'undefined' &&
    document.visibilityState !== 'visible'
  ) {
    return;
  }

  if (isEmbeddedInLobby()) {
    await requestEmbeddedSessionKeepAlive();
    await reconcileEmbeddedSession(hidePopup);
    return;
  }

  await ensureInitialization(hidePopup);

  let step: 'keepAlive' | 'getSession' = 'keepAlive';
  try {
    await Account.keepAlive();

    const { loginToken } = getAWAuthTokens();
    if (loginToken) {
      step = 'getSession';
      await Account.getSessionAsync({ loginToken });
    }

    Logger.logRumAction({
      name: '[GW][SESSION] Force update',
      context: { trigger, hadLoginToken: Boolean(loginToken) },
      useCase: DdEventCategory.Session,
      eventType: DdEventType.Info,
    });
  } catch (error) {
    Logger.logRumError({
      name: '[GW][SESSION] Force update failed',
      context: {
        trigger,
        step,
        error: error instanceof Error ? error.message : String(error),
        ...getSessionTokenContext(),
      },
      useCase: DdEventCategory.Session,
      eventType: DdEventType.Info,
    });
  }
};

export const useAccountUm = ({
  bootstrap = false,
}: UseAccountUmOptions = {}) => {
  const { hidePopup } = usePopup();
  const hasActiveSession = useStore(
    (state) => state.userSessionContext.hasActiveSession,
  );

  const keepSessionAlive = useCallback(async () => {
    const latestGameError = useStore.getState().gameError?.type;
    if (latestGameError === ERROR_TYPE.SESSION_EXPIRED) return;

    if (isEmbeddedInLobby()) {
      await requestEmbeddedSessionKeepAlive();
      await reconcileEmbeddedSession(hidePopup);
      return Account.keepAlive();
    }

    await ensureInitialization(hidePopup);
    return Account.keepAlive();
  }, [hidePopup]);

  useEffect(() => {
    if (!bootstrap) {
      return;
    }

    void initializeAccountUM(hidePopup);
  }, [bootstrap, hidePopup]);

  const getSession = async () => {
    if (isEmbeddedInLobby()) {
      const { session } = await reconcileEmbeddedSession(hidePopup);
      return session;
    }

    await ensureInitialization(hidePopup);
    return Account.getSessionAsync('Silent');
  };

  const getSessionWithLoginView = async () => {
    if (isEmbeddedInLobby()) return getSession();

    await ensureInitialization(hidePopup);
    return promptAccountLogin({
      reason: 'explicit_login_view',
      phase: SESSION_PHASE.REFRESH,
    });
  };

  const getSessionWithRegisterView = async () => {
    if (isEmbeddedInLobby()) return getSession();

    await ensureInitialization(hidePopup);
    return Account.getSessionAsync('NewUser');
  };

  const isSessionValid = async () => {
    if (isEmbeddedInLobby()) {
      const { valid } = await reconcileEmbeddedSession(hidePopup);
      return valid || useStore.getState().userSessionContext.hasActiveSession;
    }

    await ensureInitialization(hidePopup);
    return Account.checkValidSession();
  };

  const refreshSession = async (): Promise<Session | undefined> => {
    if (isEmbeddedInLobby()) {
      const { session } = await reconcileEmbeddedSession(hidePopup);
      if (session) {
        await handleSession(session, SESSION_PHASE.REFRESH, hidePopup);
      }
      return session;
    }

    const session = await getSession();
    if (session) {
      await handleSession(session, SESSION_PHASE.REFRESH, hidePopup);
    }
    return session;
  };

  const reauthenticate = async (): Promise<Session | undefined> => {
    try {
      return (await refreshSession()) ?? (await getSessionWithLoginView());
    } catch {
      return getSessionWithLoginView();
    }
  };

  const logOut = async () => {
    if (isEmbeddedInLobby()) {
      const wrapper = resolveWebWrapper();
      await wrapper?.requestLogout();
      return;
    }

    await ensureInitialization(hidePopup);
    await Promise.resolve(
      Account.clearSession(undefined, CLEAR_SESSION_REASON.OTHER),
    );
  };

  return {
    getSession,
    refreshSession,
    reauthenticate,
    getSessionWithLoginView,
    getSessionWithRegisterView,
    isSessionValid,
    keepSessionAlive,
    logOut,
    hasActiveSession,
  };
};
